import { useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Tooltip } from '../../../../components/Tooltip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdDate, TdInput, TdMoney, TdSelect } from '../../../../components/inline-add-table/cells';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import {
  LICENSE_PAYMENT_MODE,
  LICENSE_PAYMENT_MODE_LABEL,
  LICENSING_MODEL,
  LICENSING_MODEL_LABEL,
  OWNERSHIP_LABEL,
  OWNERSHIP,
  isRecurringPaymentMode,
} from '../inventoryTypes';
import type { LicensePaymentMode, LicensingModel } from '../inventoryTypes';
import { stockUnitsApi } from '../stockApi';
import type { RegisterStockUnitLine } from '../stockTypes';
import { sampleLicenseRow } from '../utils/sampleStock';

/**
 * What a licence registration is recorded AGAINST.
 *
 * Deliberately the narrowest set of fields the form reads rather than a stock level: a level row
 * satisfies it structurally, and so does an item registered five seconds ago that has no level
 * row yet. The second case is what the item form's "add licences now" tick hands over, and it is
 * the reason this is a shape rather than a type name — the same call
 * {@link import('./StockUnitsRegisterModal').StockUnitsRegisterTarget} takes for physical stock.
 *
 * `onHand` is 0 for a fresh registration, which is not a placeholder — it is what is true.
 */
export interface LicenseRegisterTarget {
  id: string;
  sku: string;
  name: string;
  inventoryCategoryName: string;
  onHand: number;

  /**
   * How the item is licensed, which is what the TENURE is read off.
   *
   * A perpetual licence is bought outright and a term licence is held for a period, so the
   * ownership type is not a question this form asks — it states the answer and the server derives
   * the same one. Null where the catalog row has not said, which the server refuses on the item
   * itself, so in practice it is always set.
   */
  licensingModel: LicensingModel | null;
}

/** One row of the grid. `key` is React's, never sent. */
interface LicenseRow {
  key: string;
  licenseNumber: string;
  acquisitionDate: string;
  termEndDate: string;
  accountablePersonnelId: string;
  unitCost: number | null;
  paymentMode: string;
  firstBillingDate: string;
}

function blankRow(): LicenseRow {
  return {
    key: uid(),
    licenseNumber: '',
    acquisitionDate: '',
    termEndDate: '',
    accountablePersonnelId: '',
    unitCost: null,
    paymentMode: '',
    firstBillingDate: '',
  };
}

/** `firstBillingDate` → `first-billing-date`, matching `focusValidationError`'s own kebab. */
function kebab(field: string): string {
  return field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * The CONTRACT with the server, and both ends have to stay identical.
 *
 * A registration row has no field a reader could otherwise be pointed at, so the server rejects
 * a cell as `units.2.serialNumber` and every cell here carries the matching id.
 * `focusValidationError` joins them with `${idPrefix}-${kebab(field)}`. Change either side and
 * the reader is told which cell is wrong while nothing moves — which reads as the feature not
 * being wired at all.
 *
 * <b>The column named `serialNumber` is drawn as "Licence no."</b>, which is the contained
 * label/entity split this suite already takes: the wire field, the column and every rejection
 * keep the schema's word, and the reader gets theirs.
 */
const cellId = (index: number, column: string) =>
  `license-register-units.${index}.${kebab(column)}`;

/**
 * Register SEVERAL licence seats of one digital asset at once.
 *
 * <b>Product call, 23 Sep 2026.</b> A digital asset's stock used to be registered through
 * <c>StockUnitsRegisterModal</c>, whose grid is a delivery of physical things — a facility per
 * row, a serial, a cost — and which asked for an ownership type a licence cannot answer
 * independently of how it is licensed. This is the dedicated form, and the divergences are the
 * point rather than incidental:
 *
 * - <b>NO ownership type.</b> It is DERIVED from the licensing model — perpetual is owned, a term
 *   is leased — so asking would be one fact in two slots with nothing saying which of them a
 *   reader should act on. The form STATES the answer; the server derives the same one and refuses
 *   a request that carries one, because there is no control here for a caller to have used.
 * - <b>A LICENCE ENTRY grid</b>, not a stock entry: licence no., acquired, accountable, cost,
 *   payment mode and the first billing date. Those are what a licence register is opened to
 *   answer, and a serial-and-facility grid answers none of them.
 * - <b>NO FACILITY, anywhere.</b> A licence seat sits nowhere, so there is no store to name —
 *   not per row and not on a header. `StockUnit.StorageFacilityId` is nullable for exactly this,
 *   and the server REFUSES one on a kind that does not sit anywhere. It was on the header for a
 *   few hours, explained as the "custodian store" the seats are booked against, which is a
 *   concept this product does not have — see CLAUDE.md, "A constraint that contradicts the
 *   INSTRUCTION is raised or removed".
 *
 * <b>ONE PANEL, and that is not the two-step shape its sibling took.</b> That form's first step
 * carries the tenure and the lease term, which is a stage of work. Here the tenure is derived and
 * there is no facility at all, so the step it would have held is EMPTY — nothing above the grid
 * but the item, stated.
 *
 * <b>It writes RECEIPTS</b>, one per seat, exactly as every other registration does — not the
 * adjustment the quantity side records, which says a FIGURE was wrong rather than that something
 * arrived. And it is <b>ALL-OR-NOTHING</b>: eight seats written beside a ninth refused would
 * leave the reader working out which are missing and re-keying them against a licence-number
 * check that now refuses the eight already in.
 */
export function LicenseRegisterModal({
  open,
  target,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** What the licences are of. Null closes the form. */
  target: LicenseRegisterTarget | null;
  onClose: () => void;
  onSaved: (registered: number) => void;
}) {
  /*
    The ACCOUNTABLE column is another module's data, so it is withheld without that module's read
    key rather than drawn and refused — hide, don't disable. A reader who lacks it registers
    licences with nobody named, which is a real state: the column is optional on every row.

    `stock-units` implies `personnel.read` SCOPED to create for exactly this picker, so a holder
    of the registration key has it and a mere reader of the unit list does not.
  */
  const canReadPersonnel = usePermission('personnel.read');

  const [rows, setRows] = useState<LicenseRow[]>([blankRow()]);

  const [staff, setStaff] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const itemId = target?.id ?? null;

  useEffect(() => {
    if (!open || !itemId) return;

    setRows([blankRow()]);
    setConfirmed(false);
    setError(null);
  }, [open, itemId]);

  /*
    The accountable picker resolves its NAMES through the identity service, in ONE batch call for
    the page of ids it is showing — never one request per row. That is the whole reason a
    personnel row carries a `personId` rather than a name, and it is the call the issuance form
    already makes.
  */
  useEffect(() => {
    if (!open || !itemId || !canReadPersonnel) return;

    const controller = new AbortController();
    setLoading(true);

    personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (page) => {
        const names = await personsApi.resolve(
          // A record above this reader's access level arrives with NO personId — that absence
          // is the mask — so there is nothing to resolve. The option falls back to the employee
          // number below, which identifies the row without naming the person.
          page.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          page.items.map((p) => ({
            id: p.id,
            name: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
          })),
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, itemId, canReadPersonnel]);

  const staffOptions = useMemo(
    () => staff.map((s) => ({ value: s.id, label: s.name })),
    [staff],
  );

  const paymentModeOptions = useMemo(
    () =>
      Object.entries(LICENSE_PAYMENT_MODE_LABEL).map(([value, label]) => ({ value, label })),
    [],
  );

  const patch = (key: string, next: Partial<LicenseRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...next } : row)));

  /*
    ABANDONED vs HALF-FILLED — the client's mirror of the server's own rule, and the two must not
    drift. A row nobody touched is dropped; one carrying a date, a cost or an assignee and no
    licence number is refused, because that one is a row somebody started.
  */
  const isAbandoned = (row: LicenseRow) =>
    row.licenseNumber.trim() === '' &&
    row.acquisitionDate === '' &&
    row.termEndDate === '' &&
    row.accountablePersonnelId === '' &&
    row.unitCost === null &&
    row.paymentMode === '' &&
    row.firstBillingDate === '';

  const filled = rows.filter((row) => !isAbandoned(row));

  /*
    A row is COMPLETE when it carries the one required cell. The `+` is gated on the last row
    being complete, so a misclick cannot spawn a second blank one — and an abandoned row counts
    as complete for that purpose, because the reader is entitled to leave it and press Cancel.
  */
  const lastRow = rows[rows.length - 1];
  const lastIncomplete =
    lastRow !== undefined && !isAbandoned(lastRow) && lastRow.licenseNumber.trim() === '';

  /** Only a TERM licence has a term to end, so nothing else draws the column. */
  const tracksTerm = target?.licensingModel === LICENSING_MODEL.term;

  /**
   * The tenure, DERIVED — never asked for.
   *
   * A term licence is held for a period and a perpetual one is bought outright, which is the same
   * distinction `OwnershipType` draws. The server computes the identical value and refuses a
   * request that names one, so this is a STATEMENT on screen rather than a control.
   */
  const ownership = tracksTerm ? OWNERSHIP.leased : OWNERSHIP.owned;

  /*
    A NEW ROW CARRIES THE LAST ONE FORWARD — the acquisition date, the assignee, the cost, the
    payment mode and the first billing date.

    A licence purchase is one order: twelve seats are bought on one date at one price on one
    billing cadence, and re-answering five identical questions twelve times is the work this grid
    exists to remove. It is a SUGGESTION, seeded once and the reader's from then on.

    <b>THE LICENCE NUMBER IS NEVER CARRIED</b>, and that is the whole point of the column: it is
    held once per item, so copying it would seed a duplicate the form then refuses on the row it
    just made.

    <b>Nor is the TERM END</b>, which is the one field here that is genuinely per-seat — every
    licence lapses on its own date, which is why a subscription is serialized at all.

    It reads the last row carrying ANYTHING rather than the last row: a reader who pressed `+`
    twice would otherwise copy the blank they abandoned.
  */
  const addRow = () =>
    setRows((current) => {
      const last = [...current].reverse().find((row) => !isAbandoned(row));

      return [
        ...current,
        {
          ...blankRow(),
          ...(last === undefined
            ? {}
            : {
                acquisitionDate: last.acquisitionDate,
                accountablePersonnelId: last.accountablePersonnelId,
                unitCost: last.unitCost,
                paymentMode: last.paymentMode,
                firstBillingDate: last.firstBillingDate,
              }),
        },
      ];
    });

  /** Named for the Save tooltip. Null means nothing is in the way. */
  const blockedReason = (): string | null => {
    if (filled.length === 0) return 'Fill in at least one licence to register.';

    const incomplete = filled.findIndex((row) => row.licenseNumber.trim() === '');

    if (incomplete >= 0) {
      return `Row ${rows.indexOf(filled[incomplete]!) + 1} needs a licence number.`;
    }

    /*
      The duplicate check runs HERE as well as on the server, because a licence number repeated
      inside one registration is the one collision no index can catch before the insert — and
      telling the reader before they press Save beats a round trip that refuses the whole batch.
      Case-insensitive, because SQL Server's default collation is.
    */
    const seen = new Set<string>();

    for (const row of filled) {
      const number = row.licenseNumber.trim().toLowerCase();

      if (seen.has(number)) {
        return `Row ${rows.indexOf(row) + 1} repeats a licence number already on an earlier row.`;
      }

      seen.add(number);
    }

    if (!confirmed) return 'Tick the confirmation to register these licences.';

    return null;
  };

  const canSave = !saving && blockedReason() === null;

  const save = async () => {
    if (!target) return;

    setSaving(true);
    setError(null);

    try {
      /*
        EVERY row is sent, abandoned ones included, so the index a rejection names is the index of
        the row on screen. Dropping them here would shift every later row's number and point the
        reader at the wrong cell; the server drops them after validating.
      */
      const units: RegisterStockUnitLine[] = rows.map((row) => {
        const mode = row.paymentMode === '' ? null : (Number(row.paymentMode) as LicensePaymentMode);

        return {
          // NULL, always — a licence seat is not anywhere, and the server refuses a store on a
          // kind that does not sit somewhere. It is also what lets the server's abandoned-row
          // rule work: a row nobody touched carries nothing at all.
          storageFacilityId: null,
          serialNumber: row.licenseNumber.trim(),
          acquisitionDate: row.acquisitionDate || null,

          /*
            NO FUND ON A LICENCE, and that is this form's own six columns rather than an
            oversight. A fund is what an ICS or a PAR prints against an article of PROPERTY; a
            seat goes on neither form, so widening this grid is a product call about this form
            rather than a consequence of the issuance capture.
          */
          fund: null,
          /*
          A LICENCE CARRIES NEITHER. A seat lapses on its own term end, which this form collects
          one column along — a lease end and a lessor are what a PHYSICAL rental has, and the
          server refuses both on a licence row rather than ignoring them.
        */
        leaseEndDate: null,
        leasedFrom: null,

        termEndDate: tracksTerm ? row.termEndDate || null : null,
          accountablePersonnelId: row.accountablePersonnelId || null,
          unitCost: row.unitCost,
          paymentMode: mode,

          /*
            CLEARED where the mode bills once. The server refuses a first-billing date beside a
            one-time payment, so sending one the cell no longer draws would be the form producing
            a rejection the reader cannot see a control for.
          */
          firstBillingDate:
            mode !== null && isRecurringPaymentMode(mode) ? row.firstBillingDate || null : null,
          notes: null,
        };
      });

      const registered = await stockUnitsApi.register({
        inventoryItemId: target.id,

        /*
          NEITHER IS SENT. The tenure is derived server-side from the licensing model and a
          request naming one is refused; the lease end is the seat's own term, which travels on
          each row. Sending the derived value back would be the client asserting a fact it read
          off the item, and the two would silently diverge the day the derivation moved.
        */
        ownership: null,
        units,
      });

      onSaved(registered);
      onClose();
    } catch (caught) {
      const failure =
        caught instanceof ApiError
          ? caught
          : new ApiError('These licences could not be registered.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'license-register' });
    } finally {
      setSaving(false);
    }
  };

  if (!target) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Register licences"
      id="license-register"
      size="3xl"
      autogenerate={{
        onGenerate: () => {
          /*
            THREE rows, because one would exercise nothing this form exists for: the grid, the
            duplicate check and the row numbering all only mean something past one.

            What it leaves alone is the assignee where the reader cannot read personnel, because
            there is no option list to pick a real row from.
          */
          setRows([0, 1, 2].map(() => ({ ...blankRow(), ...sampleLicenseRow(staff, tracksTerm) })));
        },
        disabled: saving || loading,
      }}
      confirm={{
        id: 'license-register-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="license-register-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Tooltip disabledReason={blockedReason()}>
            <Button
              id="license-register-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex min-h-0 flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The ITEM and its TENURE, stated rather than asked for. The reader chose the item by
          opening a row's kebab, so a picker here could only hold one value — and the tenure is a
          consequence of how the item is licensed rather than a decision taken per registration,
          which is exactly the read-only fact the Table Display View is for.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            /*
              ONE ROW for the item's identity, joined with the ` — ` this product already uses
              to join a qualifier to a name — the same merge `<StockAdjustmentModal>` makes for
              `sku — name`, widened by the category.

              It is a deliberate departure from the Table Display View's "one fact per row":
              three rows to restate which item the form was opened FOR pushed the fields the
              reader actually came to fill further down, and none of the three identifies the
              item on its own.
            */
            {
              label: 'Item',
              value: `${target.sku} — ${target.name} — ${target.inventoryCategoryName}`,
              mono: true,
            },
            {
              label: 'Licensing',
              value:
                target.licensingModel === null
                  ? null
                  : `${LICENSING_MODEL_LABEL[target.licensingModel]} — held as ${OWNERSHIP_LABEL[ownership].toLowerCase()}`,
            },
            { label: 'Recorded', value: `${target.onHand}`, mono: true },
          ]}
        />

        {/*
          NO FACILITY FIELD. A licence seat is not kept anywhere, so there is no store to ask for
          — see the component's own remarks. The column is nullable on the server and a request
          naming one is refused, which is what keeps this an absence rather than a field somebody
          could put back without noticing what it would mean.
        */}

        <InlineAddTable
          headers={[
            'Licence no. *',
            'Acquired',
            ...(tracksTerm ? ['Term ends'] : []),
            ...(canReadPersonnel ? ['Accountable'] : []),
            'Cost',
            'Payment mode',
            'First billing',
          ]}
          colWidths={[
            190,
            140,
            ...(tracksTerm ? [140] : []),
            ...(canReadPersonnel ? [190] : []),
            130,
            160,
            140,
          ]}
          resizeKey="license-register"
          flexColumn={0}
          rowCount={rows.length}
          onAddRow={addRow}
          addDisabled={saving || lastIncomplete}
          addButtonId="license-register-add"
        >
          {rows.map((row, index) => {
            const mode =
              row.paymentMode === '' ? null : (Number(row.paymentMode) as LicensePaymentMode);

            /*
              A ONE-TIME PAYMENT HAS NO SCHEDULE TO START, so the cell is disabled for STATE
              rather than hidden — a column that appeared and vanished per row would shift every
              cell beside it as the reader worked down the grid. The server refuses a date beside
              such a mode and clears one that arrives anyway.
            */
            const billsAgain = mode !== null && isRecurringPaymentMode(mode);

            return (
              <InlineRow
                key={row.key}
                idx={index + 1}
                onRemove={() =>
                  setRows((current) => {
                    const next = current.filter((r) => r.key !== row.key);

                    // Never nothing: an empty grid has no `+` row of its own to press, so
                    // removing the last row would leave no way back to a first one.
                    return next.length > 0 ? next : [blankRow()];
                  })
                }
              >
                {/* The wire field is `serialNumber` — see `cellId`. The reader's word is
                    "licence number", and the id keeps the schema's so a rejection can land. */}
                <TdInput
                  id={cellId(index, 'serialNumber')}
                  ariaLabel={`Licence number for row ${index + 1}`}
                  value={row.licenseNumber}
                  onChange={(next) => patch(row.key, { licenseNumber: next })}
                  placeholder="Licence no."
                  disabled={saving}
                />

                <TdDate
                  id={cellId(index, 'acquisitionDate')}
                  ariaLabel={`Acquired date for row ${index + 1}`}
                  value={row.acquisitionDate}
                  onChange={(next) => patch(row.key, { acquisitionDate: next })}
                  disabled={saving}
                />

                {/* Withheld on a perpetual licence — it never lapses, so there is no term to
                    end. A cell drawn without its header would shift every column after it. */}
                {tracksTerm ? (
                  <TdDate
                    id={cellId(index, 'termEndDate')}
                    ariaLabel={`Term end date for row ${index + 1}`}
                    value={row.termEndDate}
                    onChange={(next) => patch(row.key, { termEndDate: next })}
                    disabled={saving}
                  />
                ) : null}

                {/* Another module's data: withheld without its read key rather than drawn and
                    refused. The column is optional, so a reader without it still registers. */}
                {canReadPersonnel ? (
                  <TdSelect
                    id={cellId(index, 'accountablePersonnelId')}
                    ariaLabel={`Accountable employee for row ${index + 1}`}
                    value={row.accountablePersonnelId || null}
                    onChange={(next) => patch(row.key, { accountablePersonnelId: next })}
                    options={staffOptions}
                    remote
                    disabled={saving}
                  />
                ) : null}

                <TdMoney
                  id={cellId(index, 'unitCost')}
                  ariaLabel={`Cost for row ${index + 1}`}
                  value={row.unitCost}
                  onChange={(next) => patch(row.key, { unitCost: next })}
                  disabled={saving}
                />

                <TdSelect
                  id={cellId(index, 'paymentMode')}
                  ariaLabel={`Payment mode for row ${index + 1}`}
                  value={row.paymentMode || null}
                  onChange={(next) =>
                    patch(row.key, {
                      paymentMode: next,

                      /*
                        CLEARED the moment the mode stops billing again, rather than left to be
                        refused on save. A date surviving a switch to One-time is the value the
                        reader cannot see and did not intend — the same reason the server clears
                        it as well as refusing it.
                      */
                      firstBillingDate:
                        next !== '' && isRecurringPaymentMode(Number(next) as LicensePaymentMode)
                          ? row.firstBillingDate
                          : '',
                    })
                  }
                  options={paymentModeOptions}
                  disabled={saving}
                />

                <TdDate
                  id={cellId(index, 'firstBillingDate')}
                  ariaLabel={`First billing date for row ${index + 1}`}
                  value={row.firstBillingDate}
                  onChange={(next) => patch(row.key, { firstBillingDate: next })}
                  disabled={saving || !billsAgain}
                />
              </InlineRow>
            );
          })}
        </InlineAddTable>

        {/*
          The sentences the grid cannot carry in a header. Each is load-bearing: what a licence
          number is held against, where the tenure came from, and why a first billing date is
          dead on some rows.
        */}
        <p className="text-xs text-slate-500">
          A licence number is held once per item, so each row names a different one. Every seat
          registered here is received into stock and is held as{' '}
          {OWNERSHIP_LABEL[ownership].toLowerCase()} because the item is licensed{' '}
          {target.licensingModel === null
            ? 'that way'
            : LICENSING_MODEL_LABEL[target.licensingModel].toLowerCase()}
          . A first billing date applies only where the payment mode bills again — it is not
          asked for on a {LICENSE_PAYMENT_MODE_LABEL[LICENSE_PAYMENT_MODE.oneTime].toLowerCase()}{' '}
          purchase.
        </p>
      </div>
    </Modal>
  );
}
